/**
 * Loose-free quadtree spatial index over world-space (XY) AABBs.
 *
 * Direct adaptation of the OpenCADStudio reference implementation
 * (src/scene/pick/quadtree.rs + EntityIndex), carrying its two hard-won
 * invariants:
 *  - Straddling items stay in the parent node. Subdividing them anyway
 *    orphans geometry (an item overlapping two children would vanish from
 *    every single-child subtree query), so containment-split only.
 *  - Every item is reachable from every query. Items outside the root
 *    rectangle (or with a non-finite AABB) land in an `overflow` bag that
 *    each query walks — degenerate geometry degrades to linear, never to
 *    invisible.
 *
 * Removal is O(1) through a handle → (nodeIndex, itemIndex) locator with
 * swap-remove bookkeeping (the moved item's locator entry is rewritten).
 * No rebalancing/empty-node merging (matching OCS): leaves may end up
 * sparse after deletions, which is harmless for query cost.
 */

/** Axis-aligned bounding box in world XY: [minX, minY, maxX, maxY]. */
export type AABB = readonly [number, number, number, number];

/** Leaf capacity before subdivision (OCS LEAF_CAPACITY). */
export const LEAF_CAPACITY = 32;
/** Maximum tree depth (OCS MAX_DEPTH) — leaves below this just grow. */
export const MAX_DEPTH = 16;

/** Root padding: span * 1e-9 + 1e-6 (OCS root pad formula). */
export function padRootBounds(minX: number, minY: number, maxX: number, maxY: number): AABB {
    const spanX = maxX - minX;
    const spanY = maxY - minY;
    const span = Math.max(spanX, spanY);
    const pad = span * 1e-9 + 1e-6;
    return [minX - pad, minY - pad, maxX + pad, maxY + pad];
}

export function isFiniteAABB(b: AABB): boolean {
    return (
        Number.isFinite(b[0]) && Number.isFinite(b[1]) &&
        Number.isFinite(b[2]) && Number.isFinite(b[3]) &&
        b[0] <= b[2] && b[1] <= b[3]
    );
}

export function aabbIntersects(a: AABB, b: AABB): boolean {
    return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
}

export function aabbContains(outer: AABB, inner: AABB): boolean {
    return (
        inner[0] >= outer[0] && inner[1] >= outer[1] &&
        inner[2] <= outer[2] && inner[3] <= outer[3]
    );
}

export function aabbOfPoints(points: readonly (readonly [number, number])[]): AABB | null {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of points) {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }
    return minX > maxX ? null : [minX, minY, maxX, maxY];
}

export interface SpatialItem {
    /** Stable entity handle (the CAD entity id). */
    handle: string;
    bounds: AABB;
}

interface TreeNode {
    bounds: AABB;
    /** null while the node is a leaf; allocated on subdivision. */
    children: (number | null)[] | null;
    /** Leaf items; on branch nodes only the straddling items. */
    items: SpatialItem[];
    depth: number;
}

export interface QuadTreeStats {
    nodes: number;
    indexed: number;
    overflow: number;
    maxDepth: number;
}

const ROOT_INDEX = 0;

export class QuadTree {
    private nodes: TreeNode[] = [];
    private root: number = -1;
    /** handle → [nodeIndex, itemIndex]; OVERFLOW_SLOT marks the overflow bag. */
    private locator = new Map<string, [number, number] | 'overflow'>();
    private overflow = new Map<string, SpatialItem>();

    constructor(rootBounds?: AABB) {
        if (rootBounds && isFiniteAABB(rootBounds)) this.resetRoot(rootBounds);
    }

    /** Rebuild from scratch around the given root rectangle. */
    resetRoot(rootBounds: AABB): void {
        this.nodes = [];
        this.locator.clear();
        this.overflow.clear();
        this.root = ROOT_INDEX;
        this.nodes.push({ bounds: rootBounds, children: null, items: [], depth: 0 });
    }

    get empty(): boolean {
        return this.root === -1 && this.overflow.size === 0;
    }

    /** Number of items parked in the overflow bag (O(1)). */
    get overflowSize(): number {
        return this.overflow.size;
    }

    stats(): QuadTreeStats {
        let indexed = 0;
        let maxDepth = 0;
        for (const node of this.nodes) {
            indexed += node.items.length;
            if (node.depth > maxDepth) maxDepth = node.depth;
        }
        return { nodes: this.nodes.length, indexed, overflow: this.overflow.size, maxDepth };
    }

    /**
     * Insert an item. Items with a non-finite AABB or an AABB not fully
     * inside the root rectangle go to the overflow bag (each query still
     * visits them). Moving an existing handle: call remove() first, or
     * use update().
     */
    insert(item: SpatialItem): void {
        if (this.root === -1) {
            if (!isFiniteAABB(item.bounds)) {
                this.overflow.set(item.handle, item);
                this.locator.set(item.handle, 'overflow');
                return;
            }
            this.resetRoot(item.bounds);
        }
        if (!isFiniteAABB(item.bounds) || !aabbContains(this.nodes[this.root].bounds, item.bounds)) {
            this.overflow.set(item.handle, item);
            this.locator.set(item.handle, 'overflow');
            return;
        }
        let nodeIdx = this.root;
        for (;;) {
            const node = this.nodes[nodeIdx];
            if (node.children) {
                const child = this.childContaining(node, item.bounds);
                if (child === -1) {
                    // Straddler stays in the parent (OCS invariant).
                    node.items.push(item);
                    this.locator.set(item.handle, [nodeIdx, node.items.length - 1]);
                    return;
                }
                nodeIdx = child;
                continue;
            }
            node.items.push(item);
            this.locator.set(item.handle, [nodeIdx, node.items.length - 1]);
            if (node.items.length > LEAF_CAPACITY && node.depth < MAX_DEPTH) {
                this.subdivide(nodeIdx);
            }
            return;
        }
    }

    /** O(1) removal via locator + swap-remove. Returns false when absent. */
    remove(handle: string): boolean {
        const slot = this.locator.get(handle);
        if (!slot) return false;
        this.locator.delete(handle);
        if (slot === 'overflow') {
            this.overflow.delete(handle);
            return true;
        }
        const [nodeIdx, itemIdx] = slot;
        const node = this.nodes[nodeIdx];
        const last = node.items.length - 1;
        if (itemIdx !== last) {
            const moved = node.items[last]!;
            node.items[itemIdx] = moved;
            this.locator.set(moved.handle, [nodeIdx, itemIdx]);
        }
        node.items.pop();
        return true;
    }

    /** Remove + re-insert with fresh bounds (keeps the locator hot path). */
    update(handle: string, bounds: AABB): void {
        this.remove(handle);
        this.insert({ handle, bounds });
    }

    /** All handles whose AABB intersects the query rectangle. */
    queryRect(rect: AABB): string[] {
        const out: string[] = [];
        // Overflow always surfaces (OCS contract: non-finite/out-of-root
        // geometry is never lost to a query).
        this.overflow.forEach((item) => {
            if (aabbIntersects(item.bounds, rect)) out.push(item.handle);
        });
        if (this.root === -1 || this.nodes.length === 0) return out;
        const stack: number[] = [this.root];
        while (stack.length > 0) {
            const idx = stack.pop()!;
            const node = this.nodes[idx];
            if (!aabbIntersects(node.bounds, rect)) continue;
            for (const item of node.items) {
                if (aabbIntersects(item.bounds, rect)) out.push(item.handle);
            }
            if (node.children) {
                for (const child of node.children) {
                    if (child !== null) stack.push(child);
                }
            }
        }
        return out;
    }

    /** Index of the single child fully containing `bounds`, or -1 (straddler). */
    private childContaining(node: TreeNode, bounds: AABB): number {
        if (!node.children) return -1;
        const midX = (node.bounds[0] + node.bounds[2]) / 2;
        const midY = (node.bounds[1] + node.bounds[3]) / 2;
        const [minX, minY, maxX, maxY] = bounds;
        if (maxX <= midX) {
            if (maxY <= midY) return node.children[2]!; // SW
            if (minY >= midY) return node.children[0]!; // NW
            return -1;
        }
        if (minX >= midX) {
            if (maxY <= midY) return node.children[3]!; // SE
            if (minY >= midY) return node.children[1]!; // NE
            return -1;
        }
        return -1;
    }

    /** Allocate the four children and push fully-contained items down. */
    private subdivide(nodeIdx: number): void {
        const node = this.nodes[nodeIdx];
        const [minX, minY, maxX, maxY] = node.bounds;
        const midX = (minX + maxX) / 2;
        const midY = (minY + maxY) / 2;
        node.children = [
            this.nodes.length,     // NW
            this.nodes.length + 1, // NE
            this.nodes.length + 2, // SW
            this.nodes.length + 3, // SE
        ];
        const childDepth = node.depth + 1;
        this.nodes.push({ bounds: [minX, midY, midX, maxY], children: null, items: [], depth: childDepth });
        this.nodes.push({ bounds: [midX, midY, maxX, maxY], children: null, items: [], depth: childDepth });
        this.nodes.push({ bounds: [minX, minY, midX, midY], children: null, items: [], depth: childDepth });
        this.nodes.push({ bounds: [midX, minY, maxX, midY], children: null, items: [], depth: childDepth });

        const kept: SpatialItem[] = [];
        for (const item of node.items) {
            const child = this.childContaining(node, item.bounds);
            if (child === -1) {
                kept.push(item);
            } else {
                const c = this.nodes[child]!;
                c.items.push(item);
                this.locator.set(item.handle, [child, c.items.length - 1]);
            }
        }
        node.items = kept;
    }
}
